<template>
  <div>
    <AnimatedBackground></AnimatedBackground>
    <div class="viewport viewport-with-bottom-nav viewport-compact" style="z-index: 1">
      <div class="pt-4 d-flex justify-content-between">
        <div class="text-body">
          <Logo></Logo>
        </div>
        <div class="ml-auto mr-2" style="width: 5vw;" v-if="showFrontendSelection">
          <label for="frontendSelection" class="sr-only">Select frontend</label>
          <select v-model="selectedFrontend" class="custom-select custom-select-translucent mt-3" @change="onFrontendSelected"
                  id="frontendSelection">
            <option v-for="(availableUrl, index) in availableFrontendUrls"
                    :key="index" :value="availableUrl" :selected="selectedFrontend === availableUrl">{{ availableUrl }}</option>
          </select>
        </div>
        <LanguageSelector/>
      </div>
      <WebVersionBanner></WebVersionBanner>
      <div>
        <router-link :to="{name: 'loan-form', query: openInBrowser}" class="btn btn-lg btn-primary btn-block mb-3"
                     id="button-apply-for-loan" :class="{disabled: loading || !isLoanApplicationEnabled}">
          {{ $t('HomePage.applyForALoan') }}
        </router-link>
        <button id="button-login" class="btn btn-lg btn-secondary btn-block" @click="onLoginClick">
          <template v-if="loading">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </template>
          <template v-else>
            {{ $t('HomePage.login') }}
          </template>
        </button>
      </div>
    </div>
    <BottomNav></BottomNav>
  </div>
</template>

<script>
import Logo from '@shared/components/Logo'
import AnimatedBackground from '@shared/components/AnimatedBackground'
import store from '@shared/store'
import BottomNav from '../components/BottomNav'
import WebVersionBanner from '@shared/components/WebVersionBanner'
import settings from '@shared/settings'
import Auth from '@aws-amplify/auth'
import AnalyticsMixin from '@shared/mixins/AnalyticsMixin'
import UserMixin from '@shared/mixins/UserMixin'
import LanguageSelector from '@shared/components/LanguageSelector'
import { isAuthenticated } from '@shared/utils/routerHelpers'

export default {
  name: 'HomePage',
  components: {
    LanguageSelector,
    WebVersionBanner,
    BottomNav,
    AnimatedBackground,
    Logo
  },
  mixins: [UserMixin, AnalyticsMixin],
  data() {
    return {
      state: store.state,
      selectedFrontend: window.location.href.replace(/\/home/, ''),
      loading: false
    }
  },
  async mounted() {
    if (await isAuthenticated()) {
      await this.$router.replace({ name: 'customer-overview' })
    }
  },
  created() {
    this.$root.$on('signed', this.onSigned)
    this.$root.$on('fallback-to-otp', this.onFallbackToOtp)
    this.$root.$on('user-cancelled', this.onFallbackToOtp)
    this.$root.$on('not-available', this.onFallbackToOtp)
    // this.$root.$on('authentication-failed', this.onFallbackToOtp)
  },
  destroyed() {
    this.$root.$off('signed', this.onSigned)
    this.$root.$off('fallback-to-otp', this.onFallbackToOtp)
    this.$root.$off('user-cancelled', this.onFallbackToOtp)
    this.$root.$off('not-available', this.onFallbackToOtp)
    // this.$root.$off('authentication-failed', this.onFallbackToOtp)
  },
  computed: {
    openInBrowser() {
      // return settings.isAppToBrowserRedirectEnabled() ? { w: '' } : {}
      return {}
    },
    tryBiometryLoginOption() {
      return settings.isPkiSigningSupported() && settings.featureFlags.isBiometricLoginEnabled()
    },
    availableFrontendUrls() {
      return settings.availableFrontendUrls()
    },
    showFrontendSelection() {
      return this.availableFrontendUrls.length > 0
    },
    isLoanApplicationEnabled() {
      return settings.featureFlags.isLoanApplicationEnabled()
    }
  },
  methods: {
    onLoginClick() {
      const biometryUser = store.getBiometryUser()
      if (this.tryBiometryLoginOption && store.getLoginWithBiometryEnabled() && biometryUser) {
        this.loading = true
        this.digest = btoa(`login:${new Date().toISOString()}`)
        const parameters = JSON.stringify({
          ...biometryUser,
          labels: this.biometryLabels('login'),
          dataToSign: this.digest
        })

        if (settings.isAndroid()) {
          window.publicKeyInfrastructure.sign(parameters)
        } else if (settings.isIOS()) {
          window.webkit.messageHandlers.pkiSign.postMessage(parameters)
        }
      } else {
        this.goToLoginPage()
      }
    },
    biometryLabels(prefix) {
      return {
        title: this.$t(`biometry.${prefix}.title`),
        subtitle: this.$t(`biometry.${prefix}.subtitle`),
        description: this.$t(`biometry.${prefix}.description`),
        cancel: this.$t(`biometry.${prefix}.cancel`)
      }
    },
    onSigned(payload) {
      const username = store.getBiometryUser().pin

      return this.$http.post(`/api/pki/${username}/biometry-login`, {
        digest: this.digest,
        signature: payload.signature
      }).then(() => {
        return Auth.signIn(username)
            .then((cognitoUser) => {
              const tokenClaims = {
                signature: payload.signature,
                digest: this.digest,
                verification_host: settings.backendUrl().replaceAll('https://', '')
              }
              return Auth.sendCustomChallengeAnswer(cognitoUser, payload.signature, tokenClaims)
                  .then(({ signInUserSession }) => {
                    if (!signInUserSession) {
                      throw new Error('User authentication failed')
                    }
                    this.logUserSignedIn(username)
                    this.getAndStoreCustomerProfile(username).then(() => {
                      store.clearUserPinForOTP()
                      this.$router.replace({ name: 'no-back', params: { page: 'customer-overview' } })
                    })
                  })
                  .catch(this.onBiometryLoginFailed)
            })
            .catch(this.onBiometryLoginFailed)
      }).catch(this.onBiometryLoginFailed)
    },
    onBiometryLoginFailed(e) {
      console.warn(JSON.stringify(e), JSON.stringify(store.getBiometryUser()))
      this.$errorToast(this.$t('errors.biometry.title'), this.$t('errors.biometry.content'))
      this.onFallbackToOtp()
    },
    onFallbackToOtp() {
      this.loading = false
      this.goToLoginPage()
    },
    goToLoginPage() {
      this.$router.push({ name: 'login' })
    },
    onFrontendSelected() {
      if (settings.isAndroid() && window.frontendSelector) {
        window.frontendSelector.onFrontendSelected(this.selectedFrontend)
      }
      if (settings.isIOS() && window.webkit.messageHandlers.onFrontendSelected) {
        window.webkit.messageHandlers.onFrontendSelected.postMessage(this.selectedFrontend)
      }
      if (settings.isWeb()) {
        window.location.href = this.selectedFrontend
      }
    }
  }
}
</script>
